import { type ReactElement, useEffect, useMemo, useState } from "react";

import {
  Badge,
  Box,
  Button,
  ChangeIcon,
  FilterIcon,
  FilterMenu,
  FilterMenuButton,
  FilterMenuGroup,
  FilterMenuList,
  FilterMenuOption,
  PlusIcon,
  Row,
  SearchInput,
  SubtractIcon,
  Text,
  Tooltip,
} from "@hightouch/htui";
import get from "lodash/get";
import moment from "moment";
import { LinkButton, useLocation, useNavigate, useParams } from "src/router";
import { isPresent } from "ts-extras";

import {
  type DiffMode,
  isAllModeSync,
} from "@hightouch/lib/sync/diff-mode/types";
import { useUser } from "src/contexts/user-context";
import {
  type AttemptedRowsByPrimaryKeyQuery,
  type AttemptedRowsQuery,
  SyncOp,
  useTransformedSyncRunConfigurationQuery,
} from "src/graphql";
import type { SyncRequestErrorInfo } from "src/types/sync-errors";
import { SimplePagination, Table, type TableColumn } from "src/ui/table";
import { isDeprecatedSyncRunError, type SyncRunStatus } from "src/utils/syncs";
import { openUrl } from "src/utils/urls";

import { SyncRequestErrorModal } from "src/pages/syncs/sync/components/error-modals";
import ShipImage from "src/pages/syncs/sync/components/ship.svg";
import { RowExportModal } from "src/pages/syncs/sync/run/rows/export";
import { commaNumber } from "src/utils/numbers";
import type { Sync } from "src/pages/syncs/sync/utils/types";
import { PermissionedButton } from "src/components/permission";
import { omittedFieldsMessage } from "src/pages/syncs/sync/run/rows/omitted-fields";
import { useSlowSearch } from "src/pages/syncs/sync/run/rows/use-slow-search";
import { getRowLookupErrorMessage } from "src/pages/syncs/sync/run/row-lookup-error";
import { useFlags } from "launchdarkly-react-client-sdk";

type RowsProps = {
  addedRows?: number | null;
  attemptedRowsByPKData?: AttemptedRowsByPrimaryKeyQuery;
  attemptedRowsByPKError: Error | null;
  attemptedRowsByPKLoading: boolean;
  attemptedRowsData?: AttemptedRowsQuery;
  attemptedRowsLoading: boolean;
  attemptedRowsQueryError: Error | null;
  changedRows?: number | null;
  page: number;
  pages: number;
  plannerType?: string | null;
  primaryKey?: string | null;
  redirectPrefix?: string | null;
  refetchAttemptedRowsByPK: () => void;
  removedRows?: number | null;
  search: string;
  searchInput: string;
  setPage: (page) => void;
  setPageKeys: (keys) => void;
  setSearch: (search) => void;
  setSearchInput: (input) => void;
  setSyncOpFilters: (filters: Record<SyncOp, boolean>) => void;
  syncOpFilters: Record<SyncOp, boolean>;
  showRejected: boolean;
  source?: NonNullable<Sync["segment"]>["connection"];
  sync?: Sync;
  syncError: any;
  syncRequest: any;
};

export const RowsTable = ({
  addedRows,
  attemptedRowsByPKData,
  attemptedRowsByPKError,
  attemptedRowsByPKLoading,
  attemptedRowsData,
  attemptedRowsLoading,
  attemptedRowsQueryError,
  changedRows,
  page,
  pages,
  plannerType,
  primaryKey,
  redirectPrefix = "",
  refetchAttemptedRowsByPK,
  removedRows,
  search,
  searchInput,
  setPage,
  setPageKeys,
  setSearch,
  setSearchInput,
  setSyncOpFilters,
  syncOpFilters,
  showRejected,
  source,
  sync,
  syncError,
  syncRequest,
}: RowsProps) => {
  const navigate = useNavigate();
  const { run_id: runId, sync_id: syncId } = useParams<{
    run_id: string;
    sync_id: string;
  }>();

  const { appHideExportRowsButton } = useFlags();

  const diffMode = syncRequest?.diff_mode as DiffMode | null | undefined;
  const isAllMode = isAllModeSync(plannerType, diffMode);

  const [nextLoading, setNextLoading] = useState<boolean>(false);
  const [previousLoading, setPreviousLoading] = useState<boolean>(false);
  const [runError, setRunError] = useState<SyncRequestErrorInfo>();
  const [showExport, setShowExport] = useState<boolean>(false);

  // XXX: As a hack, we disable showing row-level information for regular
  // viewers for one of our customers. This is not meant to be secure, and
  // just prevents accidentally viewing PII data. We should remove this
  // once we have first-class support for this via our permissioning
  // system.
  const { user } = useUser();
  const userId = user?.id;

  const isDataProtected =
    // We don't populate the results-index for clean room sources anyway,
    // but we can show a better UI for those sources.
    Boolean(source?.definition.cleanRoomType) ||
    // This is a hack for a specific customer.
    // See https://github.com/hightouchio/hightouch/pull/2887.
    (syncId === "27126" && userId !== 63366);

  const showExportRowsButton = useMemo(() => {
    if (isDataProtected) {
      return false;
    }

    // hide the export button if the user is impersonating (requires feature flag enabled)
    if (appHideExportRowsButton && user?.is_impersonating) {
      return false;
    }

    // default we can show the button, it is permissioned as well.
    return true;
  }, [isDataProtected, appHideExportRowsButton, user]);

  const isSearchSlow = useSlowSearch(attemptedRowsByPKLoading, search);
  // A search replaces the list, so only the query feeding the table can fail it.
  const tableError = search ? attemptedRowsByPKError : attemptedRowsQueryError;

  const data = search
    ? attemptedRowsByPKData?.getAttemptedRowsByPrimaryKey?.rows
    : attemptedRowsData?.getAttemptedRows?.rows;

  const rows = useMemo(
    () =>
      (data || [])
        .filter(isPresent)
        .map(
          (
            {
              id,
              opType,
              rejectionReason,
              fields,
              batchId,
              requestInfoKeys,
              isBatchError,
              error,
              fieldsOmittedBytes,
              fieldsOmittedReason,
            },
            index,
          ) => ({
            hightouchRowIndex: index,
            hightouchRowId: id,
            opType,
            rejectionReason,
            batchId,
            requestInfoKeys,
            isBatchError,
            fields,
            error,
            hightouchFieldsOmittedBytes: fieldsOmittedBytes,
            hightouchFieldsOmittedReason: fieldsOmittedReason,
            ...JSON.parse(fields),
          }),
        ),
    [data],
  );

  // Rows are no longer available when the run is older than a week
  const isRowsDataExpired = useMemo(() => {
    if (!syncRequest || rows.length > 0) {
      return false;
    }

    const weekAgo = moment().subtract(1, "week");
    return moment(syncRequest.created_at).isBefore(weekAgo);
  }, [syncRequest, rows]);

  const rowValue = (row, key) => {
    const value = get(row, key);
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  };

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const columns: TableColumn[] = useMemo(() => {
    const columns: TableColumn[] = [
      {
        min: "130px",
        max: "130px",
        name: "Type",
        cell: ({ opType }) => syncOpTypeLabel[opType],
      },
    ];

    if (primaryKey) {
      columns.push({
        name: `${primaryKey} (primary key)`,
        cell: (row) => {
          const message = omittedFieldsMessage({
            fieldsOmittedBytes: row.hightouchFieldsOmittedBytes,
            fieldsOmittedReason: row.hightouchFieldsOmittedReason,
          });
          if (!message) return rowValue(row, primaryKey);
          // The primary key is one of the omitted fields, and the row id is only its hash, so the badge stands in for it.
          return (
            <Tooltip message={message}>
              <Badge size="sm" variant="warning">
                Fields not shown
              </Badge>
            </Tooltip>
          );
        },
      });
    }

    if (showRejected) {
      columns.push({
        name: "Error message",
        cell: ({ rejectionReason }) => {
          if (!rejectionReason) return null;

          return (
            <Box
              as={Badge}
              variant="error"
              border="none"
              maxWidth="100vw"
              justifyContent="flex-start"
            >
              <Text size="sm" color="inherit" variant="monospace" isTruncated>
                {rejectionReason}
              </Text>
            </Box>
          );
        },
      });
    }

    const row = data?.find((row) => row && !row.fieldsOmittedReason);

    if (row && !showRejected) {
      // Sort fields and remove primary key from the list
      const keys = Object.keys(JSON.parse(row.fields))
        .filter((x) => x !== primaryKey)
        .sort((a, b) => a.localeCompare(b));

      keys.forEach((key, i) => {
        columns.push({
          name: key,
          cell: (row) =>
            row.hightouchFieldsOmittedReason ? null : rowValue(row, key),
          divider: !primaryKey && i === 0,
        });
      });
    }

    return columns;
  }, [showRejected, plannerType, data]);

  const { data: prevConfig } = useTransformedSyncRunConfigurationQuery(
    { id: syncId ?? "", runId: runId ?? "" },
    {
      enabled: Boolean(runId && syncId),
      select: (data) => data.getTransformedSyncRunConfiguration,
    },
  );

  const NOT_SUPPORTED_FILE_STORAGE_CONFIGS = [
    {
      syncConfig: {
        mode: "diff",
      },
    },
    {
      syncConfig: {
        mode: "insert",
      },
    },
  ];
  const DEBUGGER_NOT_SUPPORTED_DESTINATIONS = {
    ams: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    "azure-blob-destination": NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    bluecore: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    box: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    dropbox: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    gam360: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    gcs: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    "marigold-engage-plus": [
      {
        syncConfig: {
          type: "bulk",
          mode: "insert",
        },
      },
    ],
    minio: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    onedrive: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    rockerbox: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    s3: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    sfmcFileDrop: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    sftp: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
    sharepoint: NOT_SUPPORTED_FILE_STORAGE_CONFIGS,
  };

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const isDebuggerSupported = useMemo(() => {
    if (!prevConfig) {
      return true;
    }
    if (
      !Object.keys(DEBUGGER_NOT_SUPPORTED_DESTINATIONS).includes(
        sync?.destination?.type || "",
      )
    ) {
      return true;
    }
    const unsupportedConfigs =
      DEBUGGER_NOT_SUPPORTED_DESTINATIONS[sync?.destination?.type || ""];
    if (!unsupportedConfigs) {
      return true;
    }
    for (const unsupportedConfig of unsupportedConfigs) {
      if (
        Object.keys(unsupportedConfig.syncConfig).every(
          (key) =>
            get(prevConfig || {}, key) === unsupportedConfig.syncConfig[key],
        )
      ) {
        return false;
      }
    }

    return true;
  }, [prevConfig]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (nextLoading) {
      setPage((page) => page + 1);
      setNextLoading(false);
    }
    if (previousLoading) {
      setPage((page) => page - 1);
      setPreviousLoading(false);
    }
  }, [rows, setPage, setNextLoading, setPreviousLoading]);

  const operationTypeFilters = [
    syncOpFilters.ADDED ? SyncOp.Added : null,
    syncOpFilters.CHANGED ? SyncOp.Changed : null,
    syncOpFilters.REMOVED ? SyncOp.Removed : null,
  ].filter(isPresent);

  const setOperationTypeFilters = (filters: SyncOp[]) =>
    setSyncOpFilters(
      filters.reduce<Record<SyncOp, boolean>>(
        (acc, filter) => ({ ...acc, [filter]: true }),
        { ADDED: false, CHANGED: false, REMOVED: false },
      ),
    );

  const { search: searchParams } = useLocation();

  const showSearchBar = plannerType !== "all";

  return (
    <>
      <Row
        align="center"
        justify={showSearchBar ? "space-between" : "flex-end"}
        w="100%"
        flexWrap="wrap"
        p={6}
        gap={4}
      >
        {showSearchBar && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setSearch(searchInput);
            }}
          >
            <Row align="center" gap={2}>
              <SearchInput
                placeholder={primaryKey ? `Search by ${primaryKey}` : ""}
                value={searchInput}
                onChange={(event) => {
                  const value = event.target.value;
                  setSearchInput(value);
                  if (value === "") {
                    setSearch("");
                  }
                }}
              />
              <Button type="submit">Search</Button>
              {isSearchSlow && (
                <Text size="sm" color="text.secondary" role="status">
                  Searching rows… Large runs can take a minute or more.
                </Text>
              )}
            </Row>
          </form>
        )}
        <Row align="center" gap={2}>
          {!isDataProtected &&
            syncError &&
            !isDeprecatedSyncRunError(syncError.message) && (
              <Button variant="warning" onClick={() => setRunError(syncError)}>
                View run error
              </Button>
            )}
          <FilterMenu>
            <FilterMenuButton icon={FilterIcon}>
              Operation type
            </FilterMenuButton>
            <FilterMenuList>
              <FilterMenuGroup
                title="View"
                type="checkbox"
                value={operationTypeFilters}
                onChange={(filters) =>
                  setOperationTypeFilters(filters as SyncOp[])
                }
              >
                <FilterMenuOption value={SyncOp.Added} isDisabled={!addedRows}>
                  <Row align="center" justifyContent="space-between">
                    {addLabel}
                    <Badge size="sm">{commaNumber(addedRows ?? 0)}</Badge>
                  </Row>
                </FilterMenuOption>
                <FilterMenuOption
                  value={SyncOp.Changed}
                  isDisabled={!changedRows}
                >
                  <Row align="center" justifyContent="space-between">
                    {changeLabel}
                    <Badge size="sm">{commaNumber(changedRows ?? 0)}</Badge>
                  </Row>
                </FilterMenuOption>
                <FilterMenuOption
                  value={SyncOp.Removed}
                  isDisabled={!removedRows}
                >
                  <Row align="center" justifyContent="space-between">
                    {removeLabel}
                    <Badge size="sm">{commaNumber(removedRows ?? 0)}</Badge>
                  </Row>
                </FilterMenuOption>
              </FilterMenuGroup>
            </FilterMenuList>
          </FilterMenu>
          {showExportRowsButton && (
            <Tooltip
              isDisabled={!(isAllMode || isRowsDataExpired)}
              message={
                isAllMode
                  ? "You cannot export rows for this type of sync"
                  : "You cannot export rows for sync runs older than 7 days"
              }
            >
              <PermissionedButton
                permission={{
                  v2: { resource: "workspace", grant: "can_update" },
                }}
                isDisabled={isAllMode || isRowsDataExpired}
                onClick={() => {
                  setShowExport(true);
                }}
              >
                Export rows
              </PermissionedButton>
            </Tooltip>
          )}
        </Row>
      </Row>
      <Table
        isPrivate
        primaryKey="hightouchRowIndex" // we use index rather than the rowId because the rowId is not unique
        scrollable
        columns={columns}
        data={isDataProtected ? [] : rows}
        error={Boolean(tableError)}
        loading={attemptedRowsLoading || attemptedRowsByPKLoading}
        placeholder={{
          image: isRowsDataExpired ? ShipImage : undefined,
          title: isDataProtected
            ? "The run data is protected"
            : search
              ? `No rows match your search`
              : isRowsDataExpired
                ? "Sorry, that ship has sailed"
                : isDebuggerSupported
                  ? "No operations logged"
                  : "Debugger logs are not supported for this sync",
          body: search ? (
            <Text>
              No rows with <strong>{primaryKey}</strong> equal to{" "}
              <strong>{search}</strong>
            </Text>
          ) : isRowsDataExpired ? (
            `Debugger logs are retained for 7 days`
          ) : undefined,
          button: isRowsDataExpired ? (
            <LinkButton
              variant="secondary"
              href={`${redirectPrefix}/syncs/${syncId}`}
            >
              Go back to sync
            </LinkButton>
          ) : undefined,
          error: search
            ? getRowLookupErrorMessage(attemptedRowsByPKError)
            : attemptedRowsQueryError?.message,
          errorTitle: search ? "Couldn't search this run" : undefined,
          errorActions: search ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => refetchAttemptedRowsByPK()}
            >
              Try again
            </Button>
          ) : undefined,
        }}
        onRowClick={(row, event) =>
          openUrl(
            `debug/${row?.hightouchRowId}${searchParams}`,
            navigate,
            event,
          )
        }
      />
      <SimplePagination
        nextLoading={nextLoading}
        page={page}
        pages={pages}
        previousLoading={previousLoading}
        onNext={() => {
          setNextLoading(true);
          setPageKeys((pageKeys) =>
            [
              ...pageKeys,
              attemptedRowsData?.getAttemptedRows?.nextPageKey,
            ].filter(isPresent),
          );
        }}
        onPrevious={() => {
          setPreviousLoading(true);
          setPageKeys((pageKeys) => {
            if (page === 1) {
              return [];
            } else {
              return pageKeys.slice(0, -1);
            }
          });
        }}
      />

      {/* Modal for sync-level errors */}
      <SyncRequestErrorModal
        isOpen={Boolean(runError)}
        onClose={() => setRunError(undefined)}
        sync={sync}
        syncRequestError={runError}
        syncStatus={sync?.status as SyncRunStatus}
      />
      <RowExportModal
        runId={runId || ""}
        open={showExport}
        setOpen={setShowExport}
      />
    </>
  );
};

const addLabel = (
  <Row gap={1} fontSize={20} align="center">
    <PlusIcon color="text.secondary" />
    <Text>Add</Text>
  </Row>
);

const changeLabel = (
  <Row gap={1} fontSize={20} align="center">
    <ChangeIcon color="text.secondary" />
    <Text>Change</Text>
  </Row>
);

const removeLabel = (
  <Row gap={1} fontSize={20} align="center">
    <SubtractIcon color="text.secondary" />
    <Text>Remove</Text>
  </Row>
);

export const syncOpTypeLabel: Record<SyncOp, ReactElement> = {
  [SyncOp.Added]: addLabel,
  [SyncOp.Changed]: changeLabel,
  [SyncOp.Removed]: removeLabel,
};

export const syncOpTypeBadge: Record<SyncOp, ReactElement> = {
  [SyncOp.Added]: (
    <Badge size="sm" svgIcon={PlusIcon}>
      Add
    </Badge>
  ),
  [SyncOp.Changed]: (
    <Badge size="sm" svgIcon={ChangeIcon}>
      Change
    </Badge>
  ),
  [SyncOp.Removed]: (
    <Badge size="sm" svgIcon={SubtractIcon}>
      Remove
    </Badge>
  ),
};
